<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { userModule } from '@/store/user/module'
import UIKit from '@pocketprep/ui-kit'
import SubjectCard from '@/components/Study/SubjectCard.vue'
import { questionModule } from '@/store/question/module'
import { quizModule } from '@/store/quiz/module'
import { examMetadataModule } from '@/store/examMetadata/module'
import { subscriptionModule } from '@/store/subscription/module'
import type { TCreateActiveQuizParams } from '@/store/quiz/actions'
import type { Study } from '@pocketprep/types'
import { userExamMetadataModule } from '@/store/userExamMetadata/module'
import { allowedQuestionsPerMinute } from '@/utils'
import { bundleModule } from '@/store/bundle/module'
import { upgradeModule } from '@/store/upgrade/module'

type ModeContent = {
    icon: 'stopwatch' | 'subject' | 'missedQuestions'
    name: string
    label: string
    isPremium: boolean
}

const ModalContainer = UIKit.ModalContainer
const Modal = UIKit.Modal
const Slider = UIKit.Slider
const Icon = UIKit.Icon
const PocketButton = UIKit.Button
const PocketInput = UIKit.Input

const props = defineProps<{
    mode: 'timed' | 'weakest' | 'missed'
}>()

const emit = defineEmits<{
    close: []
    start: [activeQuizParams: TCreateActiveQuizParams]
}>()

const isLoading = ref(false)
const value = ref<number | null>(null)
const slider = reactive({
    min: 0,
    max: 100,
    step: 1,
})

const isDarkMode = computed(() => {
    return userModule.state.settings.isDarkMode
})

const hasActiveSubscription = computed(() => {
    return subscriptionModule.getters.getSubscriptionForExamId()
})

const currentExamMetadata = computed(() => {
    return examMetadataModule.getters.getCurrentExamMetadata()
})

const currentExamMetadataSubjects = computed(() => {
    return examMetadataModule.state.subjectsWithLevels
})

const examMetadataById = computed(() => {
    return examMetadataModule.getters.getExamMetadataById()
})

const uemDisabledSubjects = computed(() => {
    return userExamMetadataModule.getters.getUemDisabledSubjects(
        currentExamMetadata.value?.examGuid,
        currentExamMetadataSubjects.value
    )
})

const missedQuestionCount = computed(() => {
    const serialInfoLib = questionModule.getters.getSerialQuestionInfoLib({ questionFilter: 'all' })
    return Object.values(quizModule.getters.getLatestAnswers({ questionFilter: 'all' }))
        .filter((a: Study.Cloud.IQuizAnswer & { answerDate: number }) => {
            const qInfo = serialInfoLib[a.questionSerial]
            if (qInfo) {
                return !a.isCorrect && !uemDisabledSubjects.value.includes(qInfo.subjectId)
            }
            return false
        })
        .length
})

const weakestSubject = computed(() => {
    const sortedSubjectStats = quizModule.getters.getSortedSubjectStats()
    const userDisabledSubjects = uemDisabledSubjects.value

    const foundSubject = sortedSubjectStats.find(subjectInfo => !userDisabledSubjects.includes(subjectInfo.id))
    return foundSubject || null
})

const bundle = computed(() => {
    return bundleModule.getters.getBundles()
        .find(b => b.exams.find(e => {
            const bundleExam = examMetadataById.value[e.objectId]
            return currentExamMetadata?.value && bundleExam?.examGuid === currentExamMetadata?.value.examGuid
        }))
})

const isDisabledBundle = computed(() => {
    return bundleModule.getters.isBundleDisabled(bundle.value?.name)
})

const modeContent = computed((): ModeContent => {
    if (props.mode === 'timed') {
        return {
            icon: 'stopwatch',
            name: 'Timed Quiz',
            label: 'How many minutes?',
            isPremium: false,
        }
    } else if (props.mode === 'weakest') {
        return {
            icon: 'subject',
            name: 'Weakest Subjects Quiz',
            label: 'How many questions?',
            isPremium: true,
        }
    } else {
        return {
            icon: 'missedQuestions',
            name: 'Missed Questions Quiz',
            label: 'How many questions?',
            isPremium: true,
        }
    }
})

const showUpgradeButton = computed(() => {
    if (!hasActiveSubscription.value && modeContent.value.isPremium) {
        if (props.mode === 'weakest' && !weakestSubject.value) {
            return false
        }
        if (props.mode === 'missed' && !missedQuestionCount.value) {
            return false
        }
        return true
    }
    return false
})

onMounted(async () => {
    isLoading.value = true
    await Promise.all([
        userModule.actions.fetchUserData(),
        questionModule.actions.fetchSerialQuestionInfoLib(),
        examMetadataModule.actions.fetchExamMetadata(),
        bundleModule.actions.fetchBundles(),
    ])

    if (props.mode === 'timed') {
        value.value = 5
    }

    if (props.mode === 'weakest') {
        const subject = currentExamMetadataSubjects.value.find(s => s.subjectId === weakestSubject.value?.id)
        const subjectCount = subject?.levels.reduce((acc, currentVal) => acc + currentVal, 0)
        slider.max = subjectCount && subjectCount <= 100 ? subjectCount : 100
        slider.min = 0
        value.value = subjectCount ? 5 : 0
    }

    if (props.mode === 'missed') {
        slider.min = 0
        slider.max = Math.min(missedQuestionCount.value, 100)
        value.value = Math.min(missedQuestionCount.value, 5)
    }

    isLoading.value = false
})

const inputChange = (e: string | number) => {
    value.value = Number(e)
}

const closeModal = () => {
    emit('close')
}

const startQuiz = () => {
    isLoading.value = true
    if (!Number(value.value) || value.value === 0) {
        value.value = 1
    }
    if (!hasActiveSubscription.value && props.mode !== 'timed') {
        upgradeModule.actions.openUpgradeModal({
            quizMode: props.mode,
        })
    } else if (value.value) {
        const activeQuizParams: TCreateActiveQuizParams = {
            mode: props.mode,
            questionCount: props.mode === 'timed' ? (value.value * allowedQuestionsPerMinute) : value.value,
        }
        if (props.mode === 'timed') {
            activeQuizParams.timeLimit = value.value
        } else if (props.mode === 'weakest' && weakestSubject.value) {
            activeQuizParams.subjectIds = [ weakestSubject.value.id ]
        } else if (props.mode === 'missed') {
            activeQuizParams.include = {
                incorrect: true,
            }
        }

        emit('start', activeQuizParams)
    }
    isLoading.value = false
}
</script>

<template>
    <div>
        <Portal to="modal">
            <ModalContainer
                key="quizModeSetupModal"
                :is-dark-mode="isDarkMode"
                @close="closeModal"
            >
                <template #modal>
                    <Modal
                        :show-close-button="true"
                        class="quiz-mode__modal"
                        :is-dark-mode="isDarkMode"
                        @close="closeModal"
                    >
                        <div class="quiz-mode">
                            <Icon
                                v-dark
                                :type="modeContent.icon"
                                class="quiz-mode__icon"
                            />
                            <div v-dark class="quiz-mode__quiz-mode">
                                {{ modeContent.name }}
                            </div>
                            <SubjectCard
                                v-if="mode === 'weakest'"
                                class="quiz-mode__subject"
                                :name="weakestSubject?.name"
                                :correct-count="weakestSubject?.correctCount"
                                :total-questions="weakestSubject?.totalCount"
                            />
                            <div
                                v-if="mode === 'missed'"
                                v-dark
                                class="quiz-mode__missed"
                            >
                                <div class="quiz-mode__missed-count">
                                    {{ missedQuestionCount }}
                                </div>
                                <div v-dark class="quiz-mode__missed-label">
                                    Incorrect
                                </div>
                            </div>
                            <template
                                v-if="
                                    mode === 'timed'
                                        || (weakestSubject && mode ==='weakest')
                                        || (missedQuestionCount && mode === 'missed')
                                "
                            >
                                <div class="quiz-mode__label">
                                    {{ modeContent.label }}
                                </div>
                                <PocketInput
                                    :modelValue="value?.toString() ?? null"
                                    v-dark
                                    class="quiz-mode__input"
                                    :is-dark-mode="isDarkMode"
                                    :min="slider.min"
                                    :max="slider.max"
                                    @update:modelValue="inputChange"
                                />
                                <Slider
                                    v-if="value !== null"
                                    v-model="value"
                                    :min="slider.min"
                                    :max="slider.max"
                                    :step="slider.step"
                                    class="quiz-mode__slider"
                                    :is-dark-mode="isDarkMode"
                                />
                            </template>
                            <div v-if="!missedQuestionCount && mode === 'missed'" class="quiz-mode__no-missed">
                                <div class="quiz-mode__no-missed-title">
                                    You don’t have any missed questions.
                                </div>
                                <div class="quiz-mode__no-missed-subtitle">
                                    Impressive!
                                </div>
                            </div>
                            <div v-if="!weakestSubject && mode ==='weakest'" class="quiz-mode__no-missed">
                                <div class="quiz-mode__no-missed-title">
                                    No Weakest Subject
                                </div>
                                <div class="quiz-mode__no-missed-subtitle">
                                    Take more quizzes to determine your weakest subject.
                                </div>
                            </div>
                            <div class="quiz-mode__buttons">
                                <PocketButton
                                    :is-dark-mode="isDarkMode"
                                    type="secondary"
                                    @click="closeModal"
                                >
                                    Close
                                </PocketButton>
                                <PocketButton
                                    v-if="showUpgradeButton"
                                    :is-dark-mode="isDarkMode"
                                    :disabled="isDisabledBundle"
                                    :is-loading="isLoading"
                                    @click="startQuiz"
                                >
                                    Upgrade to Premium
                                </PocketButton>
                                <PocketButton
                                    v-if="hasActiveSubscription || !modeContent.isPremium"
                                    :is-dark-mode="isDarkMode"
                                    :disabled="((!weakestSubject && mode ==='weakest')
                                        || (!missedQuestionCount && mode === 'missed')
                                    ) || value === 0"
                                    :is-loading="isLoading"
                                    @click="startQuiz"
                                >
                                    Start Quiz
                                </PocketButton>
                            </div>
                        </div>
                    </Modal>
                </template>
            </ModalContainer>
        </Portal>
    </div>
</template>

<style lang="scss" scoped>
.quiz-mode {
    width: 492px;
    text-align: center;
    padding: 34px 10px 32px;
    box-sizing: border-box;

    @include breakpoint(black-bear) {
        max-width: 326px;
        margin: 0 auto;
        width: 100%;
    }

    &__modal {
        left: 50%;
        position: absolute;
        transform: translateX(-50%);
        top: 80px;

        @include breakpoint(black-bear) {
            width: 356px;
            height: auto;
            max-height: 638px;
            top: 50%;
            transform: translate(-50%, -50%);
        }
    }

    &__subject {
        margin: 40px auto;
    }

    &__icon {
        width: 22px;
        height: 24px;
        display: block;
        margin: 0 auto 8px;
        color: $ash;

        &--dark {
            color: $fog;
        }
    }

    &__quiz-mode {
        font-size: 18px;
        line-height: 22px;
        font-weight: 600;
        margin-bottom: 36px;
        color: $ash;

        &--dark {
            color: $fog;
        }
    }

    &__label {
        font-size: 16px;
        line-height: 15px;
        letter-spacing: 0.43px;
        margin-bottom: 14px;
    }

    &__input {
        font-size: 40px;
        font-weight: 700;
        letter-spacing: -2px;
        line-height: 50px;
        margin-bottom: 15px;

        :deep(.uikit-input__input) {
            width: 53px;
            text-align: center;
        }
    }

    &__slider {
        max-width: 335px;
        margin: 0 auto 48px;
    }

    &__no-missed {
        margin: -18px 0 40px;
    }

    &__no-missed-title {
        font-size: 15px;
        line-height: 18px;
        font-weight: 600;
        margin-bottom: 8px;
    }

    &__no-missed-subtitle {
        font-size: 14px;
        line-height: 17px;
        max-width: 300px;
        margin: 0 auto;
    }

    &__buttons {
        display: flex;
        justify-content: center;
        align-items: center;

        .uikit-btn--primary,
        .uikit-btn--secondary {
            margin: 0 4px;
        }
    }

    &__missed {
        text-align: center;
        width: 262px;
        margin: 40px auto 38px;
        padding: 13px 0 8px;
        background: $red-hint;
        border-radius: 8px;

        &--dark {
            background-color: rgba($brand-red, 0.2);
        }
    }

    &__missed-count {
        font-size: 20px;
        line-height: 15px;
        letter-spacing: 0.53px;
        margin-bottom: 3px;
        font-weight: 600;
    }

    &__missed-label {
        font-size: 13px;
        line-height: 15px;
        letter-spacing: 0.28px;
        color: $slate-03;

        &--dark {
            color: $pearl;
        }
    }
}
</style>
